import { duration, PurchaseItemStatus } from "common";
import React, { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { AnnouncementFeedback, AnnouncementType, Conversation, Installation, Plugin, Purchase } from "types";
import { api } from "../api/api";
import {
    AnnouncementsDrawer,
    getUnseenNotificationsCount,
    useNotices,
} from "../components/Announcements/AnnouncementsDrawer";
import { NotificationsDrawer } from "../components/Notifications/NotificationsDrawer";
import { Response } from "../types/Response";
import { useSeller } from "./useAccount";
import { useCollection } from "./useCollection";
import { useInvalidate } from "./useInvalidation";
import { useInstallations, usePlugins } from "./usePlugins";
import { usePurchaseNotifications } from "./usePurchaseNotifications";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { getPermissions } from "@/stores/globalStore";

interface CreateFeedbackPayload {
    payload: {
        seller: { id: ID };
        type: string;
    };
    noticeId: ID;
}

const POLLING_INTERVALS = {
    ABANDONED: duration(5, "minutes"),
    CONVERSATIONS: duration(5, "minutes"),
};

export const useNotifications = () => {
    const seller = useSeller();
    const invalidate = useInvalidate();
    const queryClient = useQueryClient();
    const { data: plugins } = usePlugins();
    const permissions = getPermissions();
    const { data: installations } = useInstallations();

    const isAbandonedPurchaseEnabled = permissions.canViewAbandoned && permissions.canViewPurchases;

    const { data: abandonPurchases } = useCollection<Purchase>(
        "abandonedPurchases",
        {
            limit: 20,
            "items.status[in]": PurchaseItemStatus.ABANDONED,
            "customerEmail[nin]": "",
            "customerEmail[exists]": true,
        },
        {
            refetchInterval: POLLING_INTERVALS.ABANDONED,
            notifyOnChangeProps: ["data"],
            enabled: isAbandonedPurchaseEnabled,
        },
    );

    const notices = useNotices({ status: "active" });
    const { data: noticeFeedbacks } = useCollection<AnnouncementFeedback>("noticeFeedbacks");
    const { unseenNotices, unSeenCount } = useMemo(() => {
        return getUnseenNotificationsCount(notices, noticeFeedbacks);
    }, [notices, noticeFeedbacks]);

    const { mutateAsync: createFeedback } = useMutation(async ({ payload, noticeId }: CreateFeedbackPayload) => {
        await api.post(`/announcements/${noticeId}/feedback?seller=${seller.id}`, payload);
    });

    const appUpdates = getAppUpdates(installations, plugins);
    const { data: conversations } = useQuery("unreadConversations", async () => getConversations(seller.id), {
        refetchInterval: POLLING_INTERVALS.CONVERSATIONS,
        notifyOnChangeProps: ["data"],
    });

    const { purchases, hasNextPage, fetchNextPage, isFetchingNextPage } = usePurchaseNotifications();

    const noticesCount = purchases.length + abandonPurchases.length + (conversations?.length ?? 0);
    const updatesCount = permissions.canViewAppStore ? appUpdates.length : 0;

    const handleAnnouncementClose = async () => {
        const payload = { seller: { id: seller.id }, type: AnnouncementType.NOTICE };
        for (const notice of unseenNotices) {
            await createFeedback({ payload, noticeId: notice.id });
        }

        invalidate("noticeFeedbacks");
    };

    const handleNoticesClose = async () => queryClient.invalidateQueries("purchaseNotifications");

    return {
        announcements: {
            title: "Announcements",
            count: unSeenCount + updatesCount,
            content: (
                <ErrorBoundary fallbackComponent={null}>
                    <AnnouncementsDrawer announcements={notices} upgrades={appUpdates} />
                </ErrorBoundary>
            ),
            onClose: handleAnnouncementClose,
        },
        notices: {
            title: "Notifications",
            count: permissions.canViewNotifications ? noticesCount : -1, // Hiding notices when permission is not granted
            content: (
                <NotificationsDrawer
                    purchases={purchases}
                    abandonedPurchases={abandonPurchases}
                    conversations={conversations ?? []}
                    hasNextPage={hasNextPage}
                    isLoading={isFetchingNextPage}
                    fetchNextPage={fetchNextPage}
                />
            ),
            onClose: handleNoticesClose,
        },
    };
};

const getConversations = async (sellerId: ID) => {
    const { data: conversations } = await api.get<Response<Conversation>>(`/conversations`, {
        params: {
            participant: sellerId,
            tag: "unread",
            sort: "updatedAt",
        },
    });

    return conversations.data ?? [];
};

export const getAppUpdates = (installations?: Installation[], plugins?: Plugin[]) => {
    const installs = [];
    if (plugins) {
        for (const plugin of plugins) {
            const installation = installations?.find((installation) => installation.plugin === plugin.id);
            if (installation && plugin.version > installation?.version && !plugin.abilities?.xolaFeature) {
                installs.push(plugin);
            }
        }
    }

    return installs;
};
